import React from "react";
import { Button, Modal, Space, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { dashboardPalette } from "../theme";

export type XpSource = {
  icon: string;
  title: string;
  description: string;
};

/**
 * Fuentes de experiencia del Desafío.
 * Editar acá alcanza para actualizar el texto en todas las pantallas.
 */
export const XP_SOURCES: XpSource[] = [
  {
    icon: "📚",
    title: "Actividades obligatorias",
    description:
      "Cada vez que aprobás una actividad obligatoria de tu cursada, sumás XP. Son el núcleo del Desafío: cuanto más comprometido estés con tu formación, más avanzás.",
  },
  {
    icon: "📝",
    title: "Parciales",
    description:
      "Aprobar un parcial tiene su recompensa. Es uno de los hitos más importantes del recorrido, así que la XP que suma es de las más valiosas.",
  },
  {
    icon: "📅",
    title: "Eventos del calendario",
    description:
      "A lo largo del año se publican eventos especiales en el calendario de la plataforma. Participar de ellos es otra forma de acumular experiencia: estar atento paga.",
  },
  {
    icon: "🎯",
    title: "Misiones",
    description:
      "De vez en cuando aparecen misiones nuevas directamente en la página. Son desafíos extra que te propone la plataforma: completarlas suma XP y le da más variedad al recorrido.",
  },
];

const INTRO =
  "La experiencia (XP) es tu progreso en el Desafío. Cuanta más acumulás, más subís de nivel y desbloqueás recompensas exclusivas.";

/**
 * Contenido plano (sin modal). Sirve para embeberlo dentro de un panel
 * de ayuda ya existente, como el de la página de Recompensas.
 */
export const XpInfoContent: React.FC<{ showIntro?: boolean }> = ({
  showIntro = true,
}) => (
  <Space direction="vertical" size={14} style={{ width: "100%" }}>
    {showIntro ? (
      <Typography.Text style={{ color: dashboardPalette.textDark }}>
        {INTRO}
      </Typography.Text>
    ) : null}

    <Typography.Text strong style={{ color: dashboardPalette.headerBlue }}>
      Hay cuatro formas de sumar XP
    </Typography.Text>

    {XP_SOURCES.map((source) => (
      <div
        key={source.title}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          borderRadius: 18,
          padding: 14,
          background: "rgba(255,255,255,0.72)",
          border: `1px solid ${dashboardPalette.panelBorder}`,
        }}
      >
        <span style={{ fontSize: 24, lineHeight: 1.2 }} aria-hidden>
          {source.icon}
        </span>
        <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
          <Typography.Text
            strong
            style={{ color: dashboardPalette.headerBlue, fontSize: 15 }}
          >
            {source.title}
          </Typography.Text>
          <Typography.Text
            style={{ color: dashboardPalette.textDark, lineHeight: 1.6 }}
          >
            {source.description}
          </Typography.Text>
        </Space>
      </div>
    ))}
  </Space>
);

/**
 * Modal completo, con la misma cabecera violeta que Notificaciones
 * y el aviso de DashboardLayout.
 */
const XpInfoModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    centered
    footer={null}
    closeIcon={<CloseOutlined style={{ color: "#fff" }} />}
    styles={{
      body: {
        padding: 0,
        borderRadius: 24,
        overflow: "hidden",
        border: `1.5px solid ${dashboardPalette.panelBorder}`,
        boxShadow: "0 20px 60px rgba(30, 27, 58, 0.18)",
      },
    }}
  >
    <div
      style={{
        background: "linear-gradient(135deg, #7c5cfc 0%, #9b7dfc 100%)",
        color: "#fff",
        padding: "16px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <Typography.Title level={4} style={{ margin: 0, color: "#fff" }}>
        ⚡ ¿Cómo ganás experiencia?
      </Typography.Title>
      <Button
        type="text"
        icon={<CloseOutlined style={{ color: "#fff" }} />}
        onClick={onClose}
      />
    </div>

    <div
      style={{
        padding: 20,
        maxHeight: "calc(78vh - 136px)",
        overflowY: "auto",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(236,240,248,0.98) 100%)",
      }}
    >
      <XpInfoContent />
    </div>
  </Modal>
);

export default XpInfoModal;
